import * as db from './indexed-db';

import { IndexedDBAccess as IndexedDbAccess } from './indexed-db-wrapper';

export type DBPromise<T> = Promise<T | null>;

export interface IApiUploadAction {
  apiUrl: string;
  body: object;
}

export type DbRecordValueType =
  string | number | object | boolean | null | undefined;

export interface DbRecord<T extends DbRecordValueType> {
  //id is ALWAYS the keypath index
  id: string;
  recordVersion?: string;
  dateCreated: Date;
  expires?: Date;
  value: T;
  uploadAction?: IApiUploadAction;
}

const masterDbName = 'agwasuite-pwa';

// a list of object stores in the database
export const stores = {
  keyValue: 'keyvalue-local',
  keyValueUpload: 'keyvalue-upload',
};

export const indexes = {
  dateCreated: 'idx_datecreated',
  recordVersion: 'idx_recordversion',
};

function createVersion1(idb: IDBDatabase) {
  db.newDbItemObjectStore(idb, {
    name: stores.keyValue,
    indexes: [
      {
        name: indexes.dateCreated,
        keyPath: ['dateCreated'],
        options: { multiEntry: false },
      },
      {
        name: indexes.recordVersion,
        keyPath: ['recordVersion'],
        options: { multiEntry: false },
      },
    ],
  });
}

class PosDb extends IndexedDbAccess {
  private _databaseName: string;
  public get databaseName(): string {
    return this._databaseName;
  }

  constructor(databaseName?: string) {
    super();
    this._databaseName = databaseName ?? masterDbName;
  }
  public async getRecord<T extends DbRecordValueType>(
    id: string,
    options?: { store?: string }
  ): DBPromise<DbRecord<T>> {
    const value = await this.getFirstMatchingRecord<DbRecord<T>>(
      options?.store ?? stores.keyValue,
      {
        query: id,
      }
    );
    if (value) {
      if (value.expires && value.expires < new Date()) {
        return null;
      }

      return value;
    }
    return null;
  }

  public async setRecord(
    id: string,
    value: DbRecordValueType,
    options?: {
      recordVersion?: string;
      store?: string;
      uploadAction?: IApiUploadAction;
    }
  ): Promise<void> {
    await this.put<DbRecord<DbRecordValueType>>(
      {
        id: id,
        value: value ?? null,
        dateCreated: new Date(),
        recordVersion: options?.recordVersion,
        uploadAction: options?.uploadAction,
      },
      options?.store ?? stores.keyValue
    );
  }

  public async pushRecordToLocalAndRemote<T extends DbRecordValueType>(
    id: string,
    value: T,
    options?: { recordVersion?: string; uploadAction: IApiUploadAction }
  ): Promise<void> {
    await this.setRecord(id, value, { recordVersion: options?.recordVersion });
    if (!options?.uploadAction) {
      throw new Error('uploadAction is required when pushing to remote');
    }
    await this.setRecord(id, value, {
      recordVersion: options?.recordVersion,
      store: stores.keyValueUpload,
      uploadAction: options?.uploadAction,
    });
  }

  public async deleteRecord(id: string): Promise<void> {
    await this.del(id, stores.keyValue);
  }
  public async deleteRecordFromUpload(id: string): Promise<void> {
    await this.del(id, stores.keyValueUpload);
  }
  public async getUploadRecords(): Promise<DbRecord<DbRecordValueType>[]> {
    return this.getAll(stores.keyValueUpload);
  }
  //return a list of upgrades that get this database to the correct version
  protected getDatabaseUpgrades(): db.DatabaseUpgrade[] {
    //add more versions as necessary to add extra tables to the db
    return [{ version: 1, execute: createVersion1 }];
  }
  public dbName(): string {
    return this._databaseName;
  }
}

export class AGWASuiteDbRegister {
  master: PosDb;
  private shops: PosDb[] = [];
  private _activeShop?: PosDb;

  constructor() {
    this.master = new PosDb();
  }

  get activeShop(): PosDb | undefined {
    return this._activeShop;
  }

  shopDb(shopName: string): PosDb {
    let shopDb = this.shops.find((x) => x.dbName() === shopName);
    if (!shopDb) {
      shopDb = new PosDb(shopName);
      this.shops.push(shopDb);
    }
    return shopDb;
  }

  setActiveShop(shopName: string): void {
    this._activeShop = this.shopDb(shopName);
  }

  prepareShops(shopNames: string[]): void {
    shopNames.forEach((shopName) => {
      this.shopDb(shopName);
    });
  }
}

const agwasuiteHost = globalThis as {
  agwasuite?: { posDb?: AGWASuiteDbRegister };
};
agwasuiteHost.agwasuite ??= {};
agwasuiteHost.agwasuite.posDb = new AGWASuiteDbRegister();
export const posDb = agwasuiteHost.agwasuite.posDb;
